// UploadRemoveDialog.jsx
import React, { useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { FileSelector } from "../../../Components/Form/FileSelector";
import { useFileUploadMutation } from "../../../redux/Organization/CreateNewCustomer/Notes/Notes";
import { useUser } from "../../../Context/UserContext";

export default function UploadRemoveDialog({
  onClose,
  onUploadSuccess,
  compKey, // expected to be like 'certificateofIncorporation_upload'
  formRef,
}) {
  const [fileValue, setFileValue] = useState(null);
  const [fileUpload, { isLoading }] = useFileUploadMutation();
  const setIsRequiredFieldFilled = useUser((s) => s.setIsRequiredFieldFilled);
  const setFormData = useUser((s) => s.setFormData);
  const setValueGlobal = useUser((s) => s.setValueGlobal);

  const handleFileChange = (fieldName, value) => {
    if (Array.isArray(value)) {
      setFileValue(value.length ? value[0] : null);
    } else {
      setFileValue(value || null);
    }
  };

  const handleRemove = () => {
    setFileValue(null);
  };

  // helper: derive docKey (strip suffix like "_upload")
  const getDocKey = (key) => {
    if (!key) return null;
    return key.replace(/_upload$/i, "");
  };

  const docKey = getDocKey(compKey);

  console.log("Dialog compKey =", compKey, " → docKey =", docKey);

  const handleUpload = async () => {
    if (!fileValue) return;

    try {
      const token = localStorage.getItem("token");
      const result = await fileUpload({ file: fileValue, token }).unwrap();
      const uploaded = result?.data;

      // We expect uploaded.exportNoteAttachmentId, uploaded.fileName, uploaded.filePath
      const fileId = uploaded?.exportNoteAttachmentId;
      const fileName = uploaded?.fileName;
      const filePath = uploaded?.filePath;

      // Build RHF/global path: kycDocument.{docKey}.{field}
      const kycBase = `kycDocument.${docKey}`;

      // ---- UPDATE RHF + GLOBAL ----
      if (formRef?.current?.setValue) {
        // write fields in a predictable structure.
        // depending on your FH schema you might set different keys; this matches the mapKycDocument lookup
        formRef.current.setValue(`${kycBase}.fileId`, fileId, {
          shouldValidate: true,
          shouldDirty: true,
        });
        formRef.current.setValue(`${kycBase}.fileName`, fileName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        formRef.current.setValue(`${kycBase}.filePath`, filePath, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (typeof setValueGlobal === "function") {
        // keep global store in sync (your setValueGlobal expects (field, value))
        setValueGlobal(`${kycBase}.fileId`, fileId);
        setValueGlobal(`${kycBase}.fileName`, fileName);
        setValueGlobal(`${kycBase}.filePath`, filePath);
      }

      // Update the top-level formData.kycDocument{ docKey: { fileId, fileName, filePath } }
      setFormData((prev) => ({
        ...prev,
        kycDocument: {
          ...(prev.kycDocument || {}),
          [docKey]: {
            fileId,
            fileName,
            filePath,
          },
        },
      }));

      setIsRequiredFieldFilled(true);

      onUploadSuccess?.({
        compKey,
        docKey,
        fileId,
        fileName,
        filePath,
      });

      setFileValue(null);
      onClose?.();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed!");
    }
  };

  return (
    <Box>
      <DialogContent>
        <FileSelector
          label="Upload Document"
          fieldName="dialogFile"
          width="50%"
          value={fileValue}
          multiple={false}
          allowedFileTypes="PDF, JPG, PNG, DOCX"
          onChange={handleFileChange}
        />

        {fileValue && (
          <Box sx={{ background: "#f5f5f5", padding: "10px", borderRadius: 6, marginTop: 2 }}>
            <Typography variant="body2">
              Selected File: <strong>{fileValue?.name || "Unnamed file"}</strong>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-start" }}>
        <Button variant="outlined" color="error" onClick={handleRemove} disabled={!fileValue || isLoading}>
          Remove
        </Button>

        <Button variant="contained" color="success" onClick={handleUpload} disabled={!fileValue || isLoading}>
          {isLoading ? "Uploading..." : "Upload"}
        </Button>

        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Close
        </Button>
      </DialogActions>
    </Box>
  );
}
